import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Pencil } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { ScheduleFilterPanel } from "./filter/filter";
import { useGetMeterScheduleList } from "@features/mx/readCycleManagement/hooks";

interface ScheduleData {
  id: string;
  readCycle: string;
  frequency: string;
  nextRunDate: string;
  lastRunDate: string;
  status: string;
  readingPeriod: string;
  utilityService: string;
  readType: string;
  runCount: number;
  createdByOn: string;
  updatedByOn: string;
}

interface ScheduleFilters {
  frequency: string;
  status: string;
  readType: string;
  startDate: string;
  endDate: string;
}

export default function ScheduleList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params
  const searchQuery = searchParams.get("search") || "";
  const frequencyFilter = searchParams.get("frequency") || "";
  const statusFilter = searchParams.get("status") || "";
  const readTypeFilter = searchParams.get("readType") || "";
  const startDateParam = searchParams.get("startDate") || "";
  const endDateParam = searchParams.get("endDate") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const { data: meterScheduleList } = useGetMeterScheduleList(
    currentPage,
    PAGE_SIZE,
    frequencyFilter,
    statusFilter,
    readTypeFilter,
    startDateParam,
    endDateParam,
    searchQuery
  );

  // Export loading state
  const [isExporting, setIsExporting] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination based on API data
  useEffect(() => {
    const totalItems = meterScheduleList?.count || 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    setPagination({
      currentPage: currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [meterScheduleList?.count, currentPage]);

  // Handle search
  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    // Reset to page 1 on new search
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Apply filters
  const handleApplyFilters = (filters: ScheduleFilters) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
    };

    if (filters.frequency && filters.frequency !== "All Frequencies") {
      newParams.frequency = filters.frequency;
    } else {
      delete newParams.frequency;
    }

    if (filters.status && filters.status !== "All Statuses") {
      newParams.status = filters.status;
    } else {
      delete newParams.status;
    }

    if (filters.readType && filters.readType !== "All Types") {
      newParams.readType = filters.readType;
    } else {
      delete newParams.readType;
    }

    if (filters.startDate) {
      newParams.startDate = filters.startDate;
    } else {
      delete newParams.startDate;
    }

    if (filters.endDate) {
      newParams.endDate = filters.endDate;
    } else {
      delete newParams.endDate;
    }

    // Reset to page 1 on filter change
    delete newParams.page;

    setSearchParams(newParams);
  };

  // Reset filters
  const handleResetFilters = () => {
    const newParams: Record<string, string> = {};
    if (searchQuery) {
      newParams.search = searchQuery;
    }
    setSearchParams(newParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  // Handle export CSV
  const handleExportCSV = () => {
    setIsExporting(true);
    console.log("Exporting schedules as CSV...");

    // Simulate export delay
    setTimeout(() => {
      setIsExporting(false);
      // Here you would typically trigger a download
    }, 1000);
  };

  // Handle actions
  const handleViewSchedule = (schedule: ScheduleData) => {
    navigate(`/mx/schedule/view/view-details/${schedule.id}`);
  };

  const handleEditSchedule = (schedule: ScheduleData) => {
    navigate(`/mx/schedule/edit/${schedule.id}`);
  };

  // Check if any filters are active
  const hasActiveFilters =
    (frequencyFilter && frequencyFilter !== "All Frequencies") ||
    (statusFilter && statusFilter !== "All Statuses") ||
    (readTypeFilter && readTypeFilter !== "All Types") ||
    startDateParam ||
    endDateParam;

  // Count active filters
  const activeFilterCount = [
    !!(frequencyFilter && frequencyFilter !== "All Frequencies"),
    !!(statusFilter && statusFilter !== "All Statuses"),
    !!(readTypeFilter && readTypeFilter !== "All Types"),
    !!startDateParam,
    !!endDateParam,
  ].filter(Boolean).length;

  // Define table columns
  const columns: TableColumn<any>[] = [
    {
      key: "meterCycleName",
      header: "Read Cycle",
      sortable: true,
    },
    {
      key: "frequencyDisplay",
      header: "Frequency",
      sortable: true,
    },
    {
      key: "nextRunDate",
      header: "Next Run Date",
      sortable: true,
    },
    {
      key: "lastRunDate",
      header: "Last Run Date",
      sortable: true,
      render: (value) => <span>{value || "-"}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            value === "Active"
              ? "bg-green-100 text-green-800"
              : value === "Completed"
              ? "bg-blue-100 text-blue-800"
              : value === "Overdue"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {value || "-"}
        </span>
      ),
    },
    {
      key: "readingPeriod",
      header: "Reading Period",
      sortable: true,
      render: (value) => (
        <span>
          {(value?.startDate || "N/A") + " - " + (value?.endDate || "N/A")}
        </span>
      ),
    },
    {
      key: "productCode",
      header: "Utility Service",
      sortable: true,
      render: (value: string, row: any) =>
        row?.productCode?.map((service: any, index: number) => (
          <span key={index}>
            {service}
            {index < row.productCode.length - 1 && ", "}
          </span>
        )),
    },
    {
      key: "meterType",
      header: "Read Type",
      sortable: true,
    },
    {
      key: "runCount",
      header: "Run Count",
      sortable: true,
      render: (value) => <span>{value || 0}</span>,
    },
     {
      key: "",
      header: "Created On",
      sortable: true,
      render: (value: string, row: any) => (
        <div>
          <p className="font-medium">{row.createdDate ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "updatedOn",
      header: "Updated On",
      sortable: true,
      render: (value: string, row: any) => (
        <div>
          <p className="font-medium">{row.lastModifiedDate ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.lastModifiedUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
  ];

  // Define actions
  const actions = [
    {
      label: "View",
      onClick: (row: ScheduleData) => handleViewSchedule(row),
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit",
      onClick: (row: ScheduleData) => handleEditSchedule(row),
      icon: <Pencil className="h-4 w-4 mr-2" />,
    },
  ];

  // Use the filter component
  const filterPanel = (
    <ScheduleFilterPanel
      frequency={frequencyFilter}
      status={statusFilter}
      readType={readTypeFilter}
      startDate={startDateParam}
      endDate={endDateParam}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
    />
  );

  return (
    <div className="space-y-6 mt-6">
      <GenericTable
        data={meterScheduleList?.results || []}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No schedules found matching your criteria"
        pagination={pagination}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search schedules..."
        filterPanel={filterPanel}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {activeFilterCount} filter(s) active
              </span>
            </div>
          ) : null
        }
        onExport={handleExportCSV}
        exportLoading={isExporting}
      />
    </div>
  );
}
